// Catalog.ID Client — Publication Status (pure logic)
// ============================================================================
// Lifecycle of a claim's *publication* — the publicclaim row's operator-review
// state — as distinct from the claim's *validation* status (certificate-badge).
// A claim can be fully validated yet still sitting in the operator review
// queue; until the review approves it, anonymous surfaces (e.g. the registry
// key-owner lookup CPR polls) return 404 for it. Keeping the two axes visually
// separate is the whole point of this module.
//
// Kept DOM-free so it is unit-testable in the node vitest environment; the
// badge element lives in publication-badge.ts.

/**
 * 'none'      — no publicclaim row: never published, or unpublished/deleted.
 * 'pending'   — submitted, awaiting operator review (publicclaim.status).
 * 'published' — operator approved; publicly resolvable.
 * 'rejected'  — operator declined; rejectionReason carries the why.
 */
export type PublicationStatus = 'none' | 'pending' | 'published' | 'rejected';

/**
 * Derive the publication status from the claim's publicclaim row, if any.
 * An unrecognised server status maps to 'none' rather than guessing — a
 * future status value should render as "no publication info", never as a
 * false "Published"/"Pending review".
 */
export function derivePublicationStatus(
    row: { status: string } | null | undefined,
): PublicationStatus {
    switch (row?.status) {
        case 'pending': return 'pending';
        case 'published': return 'published';
        case 'rejected': return 'rejected';
        default: return 'none';
    }
}

// 'Pending review' deliberately matches CPR's wording for the same server-side
// state (apps/cpr/src/client/key-status.ts) so the two UIs agree.
export const PUBLICATION_STATUS_LABELS: Record<PublicationStatus, string> = {
    none: 'Not published',
    pending: 'Pending review',
    published: 'Published',
    rejected: 'Rejected',
};

/**
 * Badge pill backgrounds for the claims-list column. The pending hue is the
 * amber CPR uses for "Pending review", NOT the cornflower blue the validation
 * badge uses for its own 'pending' — the colour difference keeps the two
 * lifecycles distinguishable at a glance. '' means no pill background.
 */
export const PUBLICATION_STATUS_COLORS: Record<PublicationStatus, string> = {
    none: '',
    pending: 'rgba(220,150,50,0.3)',
    published: 'rgba(80,200,80,0.3)',
    rejected: 'rgba(220,50,50,0.3)',
};
